import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompanyAccount } from '../queryKeys';

import { fetchCompanyAccountDeletionCheck } from './fetchCompanyAccountDeletionCheck';
import { type CompanyAccountDeletionCheckResponse } from './types';

type CompanyAccountDeletionCheckOptions<TData> = Omit<
  UseQueryOptions<CompanyAccountDeletionCheckResponse, Error, TData>,
  'queryFn' | 'queryKey'
>;

/**
 * Creates reusable query options for checking whether a company account can be deleted.
 *
 * @param parameters - The optional company account ID and additional query options.
 * @returns Query options that remain skipped until the company account ID is available.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Account/operation/check_company_account_deletion_vadmin_company_accounts__company_account_id__deletion_check_get
 */
// fallow-ignore-next-line unused-export -- intended public API surface for loaders and prefetching
export function getCompanyAccountDeletionCheckQueryOptions<
  TData = CompanyAccountDeletionCheckResponse,
>({
  companyAccountId,
  options,
}: {
  companyAccountId: UUID | undefined;
  options?: CompanyAccountDeletionCheckOptions<TData>;
}) {
  return queryOptions({
    queryFn: companyAccountId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchCompanyAccountDeletionCheck(companyAccountId, { signal })
      : skipToken,
    queryKey: queryKeysCompanyAccount.getDeletionCheck({
      companyAccountId: companyAccountId ?? '',
    }),
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Checks whether a company account can be deleted.
 *
 * @param companyAccountId - The optional company account ID to check.
 * @param options - Additional TanStack Query options.
 * @returns The company account deletion-check query result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company-Account/operation/check_company_account_deletion_vadmin_company_accounts__company_account_id__deletion_check_get
 */
export function useQueryCompanyAccountDeletionCheck<
  TData = CompanyAccountDeletionCheckResponse,
>(
  companyAccountId: UUID | undefined,
  options?: CompanyAccountDeletionCheckOptions<TData>,
) {
  return useQuery(
    getCompanyAccountDeletionCheckQueryOptions<TData>({
      companyAccountId,
      options,
    }),
  );
}
